// Text — THE small-text styles, by job. `<Text kind="note">` instead of a hand-set size and colour.
//
// Alex, 2026-10-12, after the components-grid hours total rendered at 13px beside 11px
// money totals: "I still would like something like that to avoid a lot of custom."
// Measured that day: 361 inline `fontSize` style objects in 54 files, and most were the
// same few jobs written by hand each time — a grey note (52), plain small text (45), a
// deprecated status (18), the empty-value dash (17), a hint (22), a navy mono code (14).
// Each hand-written copy is free to drift; a KIND is not.
//
// ⚠ TYPOGRAPHY IS OWNED HERE. `style` is accepted for LAYOUT only (margin, flex, width,
// whiteSpace…); fontSize / fontFamily / fontWeight / color passed in it are dropped, so
// a call site cannot quietly invent a kind of its own. A role that needs a different look is a
// new row in KINDS, named for its job, not an override.
//
// ⚠ JS, NOT CSS CLASSES: theme.css holds tokens only and every component styles inline
// from them; a kind here is just a frozen style object made of those same tokens.
//
// Values are exactly what the hand-written copies used on 2026-10-12 (`warn` added
// the same day, from DataGrid's delete/add messages), so converting a call site
// changes nothing on screen.
import { DASH } from '../icons.js';

export const KINDS = {
  // Plain small text in a cell or line; takes the surrounding colour.
  small:      { fontSize: 'var(--fs-small)' },
  // Secondary information beside the main value: units, counts, "from …" lines.
  note:       { fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' },
  // A hint or caption under or beside a control.
  hint:       { fontSize: 'var(--fs-micro)', color: 'var(--th-text-hint)' },
  // A row or value that is deprecated / inactive. Say so in words too: colour alone
  // carries no meaning for a colour-blind reader.
  deprecated: { fontSize: 'var(--fs-small)', color: 'var(--th-status-deprecated)' },
  // A warning beside an action: a delete confirm, a refused add. Lead with ⚠ or
  // a word that says it is a warning — colour alone carries no meaning.
  warn:       { fontSize: 'var(--fs-small)', color: 'var(--tg-warn-color)' },
  // A value that is not there. Renders the DASH glyph when given no children.
  empty:      { fontSize: 'var(--fs-small)', color: 'var(--th-text-faint)' },
  // A catalog / product / project code.
  code:       { fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', fontWeight: 600, color: 'var(--th-navy)' },
};

const TYPOGRAPHY = ['fontSize', 'fontFamily', 'fontWeight', 'color'];

export default function Text({ kind = 'small', as: Tag = 'span', bold = false, style = null, title, children }) {
  const base = KINDS[kind];
  if (!base) throw new Error(`Text: unknown kind "${kind}" (known: ${Object.keys(KINDS).join(', ')})`);
  const layout = {};
  if (style) for (const [k, v] of Object.entries(style)) if (!TYPOGRAPHY.includes(k)) layout[k] = v;
  const content = children ?? (kind === 'empty' ? DASH : null);
  return (
    <Tag title={title} style={{ ...layout, ...base, ...(bold ? { fontWeight: 600 } : null) }}>
      {content}
    </Tag>
  );
}
